/**
 * Centralized Endpage Constants for RespWeb
 * -----------------------------------------------------------------------------
 * Consolidates page names, event types, locale/language, region mappings,
 * template/survey constants, and experiment identifiers for the endpage system.
 * -----------------------------------------------------------------------------
 * All imports should reference this file for endpage-related constants.
 * Backwards compatibility maintained via deprecated exports.
 */

// =============================================================================
// PAGE NAVIGATION CONSTANTS
// =============================================================================
export const PAGE_NAMES = {
  SURVEY_TAKEN: 'survey taken',
  ENTERPRISE_THANK_YOU: 'enterprise thank you',
  SURVEY_CLOSED: 'survey closed',
  SURVEY_THANKS: 'survey thanks',
} as const;

export type PageName = (typeof PAGE_NAMES)[keyof typeof PAGE_NAMES];

// =============================================================================
// USER EVENT CONSTANTS
// =============================================================================
export const END_PAGE_EVENTS = {
  PAGE_VIEW: 'page viewed',
  EXPERIMENT_EVENT: 'experiment event',
  CTA_CLICK: 'cta clicked',
  SURVEY_SUBMIT: 'survey submitted',
} as const;

export type EndPageEvent = (typeof END_PAGE_EVENTS)[keyof typeof END_PAGE_EVENTS];

// =============================================================================
// METRICS & TRACKING CONSTANTS
// =============================================================================
export const METRICS_CONSTANTS = {
  APP: 'respweb',
} as const;

export const OPTIONAL_MOBILE_PATHS_EVENTS = {
  CREATE_CTA: 'optional paths v3 survey creator cta',
  CONTRIBUTE_CTA: 'optional paths v3 survey contribute cta',
  HEADER_CLICKED: 'optional paths v3 header clicked',
} as const;

export type OptionalMobilePathsEvent = (typeof OPTIONAL_MOBILE_PATHS_EVENTS)[keyof typeof OPTIONAL_MOBILE_PATHS_EVENTS];

// =============================================================================
// LOCALE & REGION CONSTANTS
// =============================================================================
export const LOCALES = {
  EN: 'en',
  FR: 'fr',
  DE: 'de',
  ES: 'es',
  IT: 'it',
  NL: 'nl',
  PT: 'pt',
  JA: 'ja',
  ZH: 'zh',
} as const;

export type Locale = (typeof LOCALES)[keyof typeof LOCALES];

export const GEO_REGIONS = {
  USA: ['US'],
  GAC: ['GB', 'AU', 'CA'],
  EUROPE: ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'CH', 'AT'],
  APAC: ['JP', 'CN', 'IN', 'SG', 'KR'],
} as const;

export type GeoRegion = keyof typeof GEO_REGIONS;

// =============================================================================
// TEMPLATE & SURVEY CONSTANTS
// =============================================================================
export const TEMPLATE_TYPES = {
  DEFAULT: 'default',
  ENTERPRISE: 'enterprise',
  THANK_YOU: 'thank_you',
  CLOSED: 'closed',
} as const;

export type TemplateType = (typeof TEMPLATE_TYPES)[keyof typeof TEMPLATE_TYPES];
